import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Progress } from "@/components/ui/progress";
import { Badge } from "@/components/ui/badge";
import { Plus, Heart, Users, Target, ArrowLeft } from "lucide-react";

type Fund = {
  id: string;
  title: string;
  story: string | null;
  category: string;
  cover_url: string | null;
  goal_cents: number;
  raised_cents: number;
  backer_count: number;
  deadline: string | null;
  status: string;
  creator_id: string;
};

const fmt = (c: number) => `$${(c / 100).toLocaleString(undefined, { maximumFractionDigits: 2 })}`;

export default function IsleFund() {
  const [funds, setFunds] = useState<Fund[]>([]);
  const [creators, setCreators] = useState<Record<string, { display_name: string; cashtag: string }>>({});
  const [loading, setLoading] = useState(true);

  const load = async () => {
    const { data } = await supabase
      .from("isle_funds")
      .select("*")
      .eq("status", "active")
      .order("created_at", { ascending: false });
    const list = (data as Fund[]) || [];
    setFunds(list);
    const ids = Array.from(new Set(list.map((f) => f.creator_id)));
    if (ids.length) {
      const { data: profs } = await supabase
        .from("profiles")
        .select("user_id, display_name, cashtag")
        .in("user_id", ids);
      const map: Record<string, { display_name: string; cashtag: string }> = {};
      (profs as any[] | null)?.forEach((p) => { map[p.user_id] = { display_name: p.display_name, cashtag: p.cashtag }; });
      setCreators(map);
    }
    setLoading(false);
  };

  useEffect(() => {
    load();
    const ch = supabase
      .channel("isle_funds_feed")
      .on("postgres_changes", { event: "*", schema: "public", table: "isle_funds" }, () => load())
      .subscribe();
    return () => {
      supabase.removeChannel(ch);
    };
  }, []);

  return (
    <div className="min-h-screen bg-background pb-24">
      <header className="sticky top-0 z-10 backdrop-blur-md bg-background/80 border-b border-border">
        <div className="max-w-2xl mx-auto px-4 py-4 flex items-center justify-between">
          <div className="flex items-center gap-3">
            <Link to="/wallet" className="p-2 -ml-2 rounded-full hover:bg-muted transition-colors" aria-label="Back to home">
              <ArrowLeft className="size-5 text-muted-foreground" />
            </Link>
            <div>
              <h1 className="text-2xl font-bold tracking-tight">Isle Fund</h1>
              <p className="text-xs text-muted-foreground">Fund dreams across the isle</p>
            </div>
          </div>
          <Button asChild size="sm">
            <Link to="/isle-fund/new"><Plus className="size-4 mr-1" />Start</Link>
          </Button>
        </div>
      </header>

      <main className="max-w-2xl mx-auto px-4 py-6 space-y-4">
        {loading ? (
          <p className="text-center text-muted-foreground py-12">Loading campaigns…</p>
        ) : funds.length === 0 ? (
          <Card>
            <CardContent className="py-12 text-center space-y-3">
              <Target className="size-10 mx-auto text-muted-foreground" />
              <p className="text-muted-foreground">No active campaigns yet.</p>
              <Button asChild><Link to="/isle-fund/new">Start the first one</Link></Button>
            </CardContent>
          </Card>
        ) : (
          funds.map((f) => {
            const pct = Math.min(100, Math.round((f.raised_cents / f.goal_cents) * 100));
            return (
              <Link key={f.id} to={`/isle-fund/${f.id}`} className="block">
                <Card className="overflow-hidden hover:border-primary transition-colors">
                  {f.cover_url && (
                    <div className="aspect-video bg-muted overflow-hidden">
                      <img src={f.cover_url} alt={f.title} className="w-full h-full object-cover" />
                    </div>
                  )}
                  <CardHeader className="pb-2">
                    <div className="flex items-start justify-between gap-2">
                      <div className="space-y-0.5 min-w-0">
                        <CardTitle className="text-lg leading-tight">{f.title}</CardTitle>
                        {creators[f.creator_id] && (
                          <p className="text-xs text-muted-foreground">by {creators[f.creator_id].display_name}</p>
                        )}
                      </div>
                      <Badge variant="secondary" className="shrink-0 capitalize">{f.category}</Badge>
                    </div>
                  </CardHeader>
                  <CardContent className="space-y-3">
                    {f.story && <p className="text-sm text-muted-foreground line-clamp-2">{f.story}</p>}
                    <Progress value={pct} className="h-2" />
                    <div className="flex items-center justify-between text-sm">
                      <span className="font-semibold">{fmt(f.raised_cents)} <span className="text-muted-foreground font-normal">of {fmt(f.goal_cents)}</span></span>
                      <span className="flex items-center gap-1 text-muted-foreground"><Users className="size-3.5" />{f.backer_count}</span>
                    </div>
                  </CardContent>
                </Card>
              </Link>
            );
          })
        )}
      </main>
    </div>
  );
}
